import React, { useEffect, useMemo, useRef } from 'react';
import { useAchievementsContext } from '../../../../contexts/AchievementsContext/AchievementsContext';
import { useBookCharactersContext } from '../../../../contexts/BookCharactersContext/BookCharactersContext';
import { useBookLocationsContext } from '../../../../contexts/BookLocationsContext/BookLocationsContext';
import { useCharacterExpressionsContext } from '../../../../contexts/CharacterExpressionsContext/CharacterExpressionsContext';
import { useCursor } from '../../../../contexts/Cursor/CursorContext';
import { useMemoryBankContext } from '../../../../contexts/MemoryBankContext/MemoryBankContext';
import { FEATURE_NODE_EDIT_SIDE_PANEL } from '../../../../helpers/featureFlags';
import { AddBranch } from './AddBranch';
import { bridgeHubOptionMods } from './bridgeHubOptionMods';

export function AddBranchContextWrapper(props) {
  const { data } = props;

  const achievements = useAchievementsContext();
  const { bookLocations, fetchBookLocations } = useBookLocationsContext();
  const expressions = useCharacterExpressionsContext();
  const { bookCharacters, fetchBookCharacters } = useBookCharactersContext();
  const { memories } = useMemoryBankContext();
  const {
    cursor,
    stepEdited,
    stepAdded,
    stepMoved,
    stepsDeleted,
    stepFocused,
    nodeEdited,
    nodeOpened,
    nodeClosed,
  } = useCursor();

  const { branch } = props;
  // `branch` prop is the full episode list (legacy name; see StoryBranches state).
  const branches = Array.isArray(branch) ? branch : [];

  const addBranchRef = useRef(null);

  const branchId = data?.id;
  // steps_count is always set by getAllV3/getChanges; steps?: BranchStep[] is optional and absent
  // in delta payloads — steps?.length ?? 0 would give 0 for non-empty nodes, corrupting initial cursor.
  const stepsCount = data?.steps_count ?? 0;

  useEffect(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) return;
    if (!branchId) return;
    nodeOpened(branchId, stepsCount > 0 ? 0 : null);
    return () => nodeClosed(branchId, stepsCount > 0 ? stepsCount : null);
  }, [branchId, stepsCount, nodeOpened, nodeClosed]);

  useEffect(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) return;
    if (cursor.branchId !== branchId) return;
    if (cursor.stepIndex === null) return;
    addBranchRef.current?.activeSteps(`step-${cursor.stepIndex}`);
  }, [cursor.branchId, cursor.stepIndex, branchId]);

  const globalContext = {
    achievements,
    memories,
    branches,
    bookLocations: bookLocations ?? [],
    fetchBookLocations,
    expressions,
    bookCharacters: bookCharacters ?? [],
    fetchBookCharacters,
  };

  const cursorEvents = useMemo(() => {
    if (!FEATURE_NODE_EDIT_SIDE_PANEL) return undefined;

    return {
      stepEdited: (stepIndex, newSteps) => stepEdited(data.id, stepIndex, { ...data, steps: bridgeHubOptionMods(newSteps) }),
      stepAdded: (stepIndex, newSteps) => stepAdded(data.id, stepIndex, { ...data, steps: newSteps }),
      stepMoved: (fromIndex, toIndex, newSteps) => stepMoved(data.id, fromIndex, toIndex, { ...data, steps: newSteps }),
      stepsDeleted: (stepIndices, newSteps) => stepsDeleted(data.id, stepIndices, { ...data, steps: newSteps }),
      stepFocused: (stepIndex) => stepFocused(data.id, stepIndex),
      nodeEdited: (patch) => {
        // LocationSelect serialises locationId as string; Number() keeps find() working against BookLocation.id.
        const locationId = Number(patch.locationId);
        const bl = bookLocations ?? [];
        const location = bl.find((loc) => loc.id === locationId) ?? null;
        nodeEdited(data.id, { ...data, ...patch, locationId, location });
      },
    };
  }, [
    data,
    bookLocations,
    stepEdited,
    stepAdded,
    stepMoved,
    stepsDeleted,
    stepFocused,
    nodeEdited,
  ]);

  return (
    <AddBranch
      {...props}
      ref={addBranchRef}
      globalContext={globalContext}
      cursorEvents={cursorEvents}
    />
  );
}
